import { useCallback, useEffect, useMemo, useState } from "react";
import MenuTop from "../../../../components/menu/top/menu-top";
import './style.css';
import { FormProps } from "../../../../shared/interfaces/form";
import Form from "../../../../components/form/form";
import { useAlert } from "../../../../components/alert/alert";
import { SEVERITY } from "../../../../shared/interfaces/alert";
import { useNavigate, useParams } from 'react-router-dom';
import { fetchUserService, updateUserService, deleteUserService, fetchUserDeleteImpactService } from "../../../../features/users/service/users-service";
import { fetchRolesWithoutAdmin } from "../../../../shared/services/roles";
import userData from "../../../../features/users/forms/user-data";
import Breadcrumb from "../../../../components/breadcrumb/breadcrumb";
import storage from "../../../../shared/services/storage";
import { fetchCompaniesService as getCompanies } from "../../../../features/company/service/company";
import InfoComponent from "../../../../components/info/info";
import { CustomModal, useModal } from "../../../../components/modals/common/index";
import { SecretariaProjectAccessController } from "../../../../features/users/logic/secretaria-project-access-controller";
import { DeleteImpactResponse } from "../../../../shared/interfaces/delete-impact";
import { DeleteImpactContent } from "../../../../components/delete-impact/delete-impact-content";

export function EditUser() {
    const { id } = useParams();
    const { showAlert } = useAlert();
    const navigate = useNavigate();
    const [formData, setFormData] = useState<FormProps[] | undefined>();
    const [rolesOptions, setRolesOptions] = useState<any[]>([]);
    const [isMaster, setIsMaster] = useState(false);
    const [deleteImpact, setDeleteImpact] = useState<DeleteImpactResponse | null>(null);
    const [isDeleteImpactLoading, setIsDeleteImpactLoading] = useState(false);
    const projectAccessController = useMemo(() => new SecretariaProjectAccessController(), []);
    
    // Hook para controlar o estado da modal de confirmação de exclusão
    const deleteModal = useModal();

    const handleEditUser = async (formData: any) => {
        if(id && formData) {
          try {
            const payload = { ...formData };

            if (isMaster) {
                projectAccessController.ensureCompanyForMaster(isMaster, payload.companyId);
            }

            const selectedProjectIds = Array.isArray(payload.projectIds) ? payload.projectIds : [];
            projectAccessController.ensureProjectSelection(payload.roleId, rolesOptions, selectedProjectIds);

            if (projectAccessController.isSecretariaRole(payload.roleId, rolesOptions)) {
                payload.projectIds = selectedProjectIds;
            } else {
                delete payload.projectIds;
            }

            const response = await updateUserService(id, payload);

            if(response) {
              showAlert('Usuário editado com sucesso', SEVERITY.SUCCESS);
              navigate('/usuarios');
            }
          } catch (error: any) {
            showAlert(error.message || 'Erro ao editar usuário', SEVERITY.ERROR);
          }
        }
    };
    
    // Função para lidar com a exclusão do usuário
    const loadDeleteImpact = useCallback(async () => {
        if (!id) return;

        setIsDeleteImpactLoading(true);
        try {
            const response = await fetchUserDeleteImpactService(id);
            setDeleteImpact(response);
        } catch (error: any) {
            setDeleteImpact(null);
            showAlert(error.message || 'Erro ao carregar vínculos do usuário', SEVERITY.ERROR);
        } finally {
            setIsDeleteImpactLoading(false);
        }
    }, [id, showAlert]);

    const handleDeleteUser = async () => {
        if (id) {
            try {
                const hasLinkedProjects = Boolean(deleteImpact?.linkedProjects?.length);
                await deleteUserService(id, hasLinkedProjects);
                showAlert('Usuário excluído com sucesso', SEVERITY.SUCCESS);
                navigate('/usuarios');
            } catch (error) {
                console.error('Erro ao excluir usuário:', error);
                showAlert('Erro ao excluir usuário', SEVERITY.ERROR);
            }
        }
    };

    const fetchRolesData = useCallback(async () => {
        try {
            const roles = await fetchRolesWithoutAdmin();
            const nextRolesOptions = roles?.map(({ name, id}: any) => ({ label: name, value: id })) || [];
            setRolesOptions(nextRolesOptions);
            return nextRolesOptions;
        } catch (error) {
            console.log(error);
        }
    }, []);

    const fetchUserData = useCallback(async (id: string) => { 
        try {
            const response = await fetchUserService(id);
            const nextRolesOptions = await fetchRolesData();
            const companies = await getCompanies();
            const user = JSON.parse(storage.getStorage('user') || '');
            const nextIsMaster = user?.name.toLowerCase() === 'cauane';
            const companyId = nextIsMaster ? response?.companyId : user?.company?.id;
            const secretariaRoleId = nextRolesOptions.find((role: any) => role.label?.toLowerCase() === 'secretaria')?.value;

            setIsMaster(nextIsMaster);
            const projectOptions = nextIsMaster
                ? await projectAccessController.loadProjectOptions(companyId)
                : await projectAccessController.loadProjectOptions(user?.company?.id);

            const data = userData(
                id,
                response,
                nextRolesOptions,
                nextIsMaster,
                companies,
                projectOptions,
                secretariaRoleId,
                (selectedCompanyId?: string) => projectAccessController.loadProjectOptions(selectedCompanyId)
            );
            setFormData(data);
        } catch (error) {
            console.log(error);
        }
    }, [fetchRolesData, projectAccessController]); 

    useEffect(() => {
        if (id) fetchUserData(id);
        fetchRolesData();
    }, [fetchUserData, fetchRolesData, id]);

    return (
        <div className="container-client">
            <MenuTop />
            <div className='container-client__content'>
                <Breadcrumb links={['Usuários', 'Editar usuário']} href={['/usuarios', '']} />
                {formData && (
                    <>
                        <InfoComponent />
                        <Form
                            data={formData}
                            onSubmitForm={handleEditUser}
                            onCancel={() => navigate('/usuarios')}
                            title={`Editar usuário`}
                            showDeleteIcon={true}
                            onClickedMenuContextType={(type: string) => {
                                if (type === 'delete') {
                                    setDeleteImpact(null);
                                    deleteModal.openModal();
                                    void loadDeleteImpact();
                                }
                            }}
                        />
                    </>
                )}
                
                {/* Modal de confirmação de exclusão */}
                <CustomModal
                    open={deleteModal.isOpen}
                    onClose={deleteModal.closeModal}
                    title="Confirmar exclusão"
                    content={
                        <DeleteImpactContent
                            entityLabel="usuário"
                            impact={deleteImpact}
                            isLoading={isDeleteImpactLoading}
                        />
                    }
                    buttons={[
                        {
                            id: 'cancel',
                            label: 'Cancelar',
                            variant: 'outlined',
                            color: 'primary',
                            onClick: deleteModal.closeModal
                        },
                        {
                            id: 'confirm',
                            label: 'Excluir',
                            variant: 'contained',
                            color: 'error',
                            onClick: () => {
                                if (isDeleteImpactLoading) return;
                                handleDeleteUser();
                                deleteModal.closeModal();
                            }
                        }
                    ]}
                />
            </div>
        </div>
    );
}
